import LoadingSpinner from '@/app/components/common/LoadingSpinner'
import SearchAutosuggestItem from '@/app/components/common/SearchAutosuggestItem'
import { Icons } from '@/app/components/ui/Icons'
import { LG_BREAKPOINT, PAGE_TYPE } from '@/app/constants'
import {
	emitLocationClickGAEvent,
	emitLocationItemSelectGAEvent,
} from '@/app/ga-event-helpers'
import { Suggestion } from '@/app/types'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useTranslations } from 'next-intl'
import React, {
	FormEvent,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import Autosuggest, {
	ChangeEvent,
	OnSuggestionSelected,
	RenderInputComponentProps,
	SuggestionSelectedEventData,
	SuggestionsFetchRequested,
} from 'react-autosuggest'
import { Tooltip } from 'react-tooltip'

export interface Props {
	defaultValue: string
	disabled?: boolean
	inputError: string | null
	suggestions: Suggestion[]
	inputClassName: string
	lang: string
	pageType: PAGE_TYPE
	onSuggestionsFetchRequested: SuggestionsFetchRequested
	onSuggestionSelected: OnSuggestionSelected<Suggestion>
	onInputChange: (value: string) => void
	onInputFocus: () => void
	onInputBlur: () => void
}

const SearchAutosuggest = forwardRef<HTMLInputElement, Props>(
	function SearchAutosuggest(
		{
			disabled,
			defaultValue,
			suggestions,
			inputError,
			inputClassName,
			lang,
			pageType,
			onSuggestionsFetchRequested,
			onSuggestionSelected,
			onInputChange,
			onInputFocus,
			onInputBlur,
		},
		ref
	) {
		const t = useTranslations('Form')
		const [value, setValue] = useState(defaultValue)
		const [isFocused, setIsFocused] = useState(false)
		const [isLoading, setIsLoading] = useState<boolean>(false)
		const inputRef = useRef<HTMLInputElement | null>(null)
		// AM-123 (M4): location_click should fire once per open episode, not on
		// every click while the field is already focused. Latched here, reset on blur.
		const locationClickEmittedRef = useRef(false)
		const isLargeViewport = useMediaQuery(LG_BREAKPOINT)

		useImperativeHandle(ref, () => inputRef.current!, [])

		const onSuggestionsFetchRequestedThrottle = useCallback(
			debounce(onSuggestionsFetchRequested, 1000),
			[]
		)

		useEffect(() => {
			setValue(defaultValue)
		}, [defaultValue])

		useEffect(() => {
			setTimeout(() => {
				if (value.length > 0 && suggestions.length === 0) {
					// emitFormNoSuggestionsFoundGAEvent(value)
				}

				setIsLoading(false)
			}, 250)
		}, [suggestions])

		useEffect(() => {
			const input = inputRef.current
			const onFocusout = () => {
				setTimeout(() => {
					onBlur()
				}, 10)
			}
			if (input) {
				input.addEventListener('focusout', onFocusout)
			}

			return () => {
				if (input) {
					input.removeEventListener('focusout', onFocusout)
				}
			}
		}, [])

		useEffect(() => {
			if (isFocused) {
				setTimeout(() => {
					inputRef.current?.focus()
					inputRef.current?.select()
				}, 1)
			}
		}, [isFocused])

		const inputProps = {
			placeholder: t('newDesign.placeholder'),
			value,
			className: inputClassName,
			onChange,
			onFocus,
			onBlur,
			onClick,
		}

		function onChange(
			_: React.FormEvent<HTMLElement>,
			{ newValue }: ChangeEvent
		) {
			setValue(newValue)
			onInputChange(newValue)
		}

		function onClick() {
			if (!locationClickEmittedRef.current) {
				emitLocationClickGAEvent()
				locationClickEmittedRef.current = true
			}
		}

		function onFocus() {
			onInputFocus()
			setIsFocused(true)

			if (!isLargeViewport) {
				setTimeout(() => {
					window.scroll(0, 10)
				}, 300)
			}
		}

		function onBlur() {
			onInputBlur()
			setIsFocused(false)
			locationClickEmittedRef.current = false
		}

		function onSuggestionClick(
			_: FormEvent,
			value: SuggestionSelectedEventData<Suggestion>
		) {
			onSuggestionSelected(_, value)
			onBlur()
			emitLocationItemSelectGAEvent(value.suggestion)
		}

		const getSuggestionValue = (suggestion: Suggestion) => {
			return suggestion.displayName
		}

		const renderSuggestion = (suggestion: Suggestion) => {
			return <SearchAutosuggestItem {...suggestion} searchTerm={value} />
		}

		const renderInputComponent = (inputProps: RenderInputComponentProps) => {
			const { id, className, ...rest } = inputProps
			return (
				<>
					<div
						className={classNames(
							'flex fixed z-40 w-full top-0 right-0 items-center p-3 h-16 justify-start lg:hidden bg-white',
							{ 'max-lg:hidden': !isFocused }
						)}
					>
						<Icons.iconClose onClick={onBlur} className="me-3" />
						<p className="font-bold text-black text-xl">{t('whereTo')}</p>
					</div>
					<div
						data-test-id="search-autosuggest-input"
						className={classNames('search-autosuggest__input-wrapper', {
							'search-autosuggest__input-wrapper--is-open': isFocused,
						})}
					>
						<div
							ref={(node) => {
								inputRef.current = node?.lastChild! as HTMLInputElement
							}}
							className="relative"
						>
							{isLoading && (
								<LoadingSpinner className="input__loading-spinner" />
							)}
							<div className="input-new__placeholder-icon">
								{isFocused && !isLargeViewport ? (
									<Icons.lensSmall />
								) : (
									<Icons.iconPin stroke={inputError ? '#D64545' : 'black'} />
								)}
							</div>
							<input
								disabled={disabled}
								{...rest}
								key={id}
								className={classNames(className, 'new-autosuggest-input h-14', {
									'!border-error':
										pageType !== PAGE_TYPE.MAIN_PAGE && inputError,
								})}
							/>
						</div>
					</div>
				</>
			)
		}

		return (
			<>
				<Autosuggest
					containerProps={{
						className: classNames(
							'new-search-autosuggest h-full',
							'search-autosuggest__container',
							{
								'search-autosuggest__container_error': inputError,
							}
						),
						...(inputError
							? {
								'data-tooltip-id': 'autosuggest-error-tooltip',
								'data-tooltip-content': inputError,
								'data-tooltip-place': 'bottom-start',
							}
							: {}),
					}}
					suggestions={suggestions}
					onSuggestionsFetchRequested={(params) => {
						if (params.value.length >= 3 && params.reason !== 'input-focused') {
							setIsLoading(true)
							onSuggestionsFetchRequestedThrottle(params)
						}
					}}
					onSuggestionSelected={onSuggestionClick}
					getSuggestionValue={getSuggestionValue}
					renderSuggestion={renderSuggestion}
					renderInputComponent={renderInputComponent}
					inputProps={inputProps}
					focusInputOnSuggestionClick={false}
				/>
				<div
					className={classNames({
						'fixed lg:hidden bg-white border-t-2 border-t-surface-2 pointer-events-none top-[105px] left-0 right-0 bottom-0 z-50':
							isFocused,
					})}
				/>
				{pageType === PAGE_TYPE.MAIN_PAGE && (
					<Tooltip
						id="autosuggest-error-tooltip"
						variant="error"
						isOpen={inputError !== null}
						className={classNames('search-autosuggest__tooltip z-30', {
							'!text-xs': lang === 'de',
						})}
					/>
				)}
			</>
		)
	}
)

export default SearchAutosuggest
